import styled, { css } from 'styled-components';
import { motion } from 'motion/react';
import { ButtonRoundedPrimary } from '../buttons/buttons';

export const BREAKPOINT_LG = '48em'; // 768px

export const Carousel = styled(motion.div)<{
	carouselHeight: number | undefined;
	carouselHeightMobile?: number;
	carouselHeightDesktop?: number;
}>`
	position: relative;
	width: 100%;
	height: ${({ carouselHeight, carouselHeightMobile }) => `${carouselHeight || carouselHeightMobile || 200}px`};
	overflow: hidden;
	a,
	button {
		z-index: 1;
	}
	@media (min-width: ${BREAKPOINT_LG}) {
		height: ${({ carouselHeight, carouselHeightDesktop }) => `${carouselHeight || carouselHeightDesktop || 300}px`};
	}
`;

export const CarouselDotsContainer = styled.div`
	position: absolute;
	bottom: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	// Trick to center absolute positioned div
	transform: translateX(-50%);
	left: 50%;
	z-index: 1;

	@media (min-width: ${BREAKPOINT_LG}) {
		bottom: 18px;
	}
`;

interface CarouselDotsProps {
	active?: boolean;
}

export const CarouselDot = styled.button<CarouselDotsProps>`
	margin: 0 5px;
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: ${({ active }) => (active ? `rgba(var(--carouselDotActiveBg), 1)` : `rgba(var(--carouselDotBg), 1)`)};
	outline: none;
`;

interface CarouselArrowProps {
	direction?: 'left' | 'right';
}

export const CarouselArrow = styled.button<CarouselArrowProps>`
	height: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	position: absolute;
	outline: none;
	z-index: 1;
	color: rgba(var(--carouselArrow), 1);

	svg {
		fill: rgba(var(--carouselArrow), 1);
		width: 24px;
		height: 24px;
		padding: 0;
	}
	${({ direction }) =>
		direction === 'left'
			? css`
					left: 0;
				`
			: css`
					right: 0;
				`}
`;

export const CarouselArrowBackground = styled.span<CarouselArrowProps>`
	background-color: var(--carouselArrowBg, rgba(var(--base-01), 1));
	border-radius: 3px;
	margin: 8px;
	padding: 8px;
	position: relative;
	top: -50px;
	@media (min-width: ${BREAKPOINT_LG}) {
		top: 0;
		width: 48px;
		height: 48px;
		border-radius: 50%;
		margin: 0;
		transform: translateX(${({ direction }) => (direction === 'left' ? '-50%' : '50%')});

		svg {
			position: absolute;
			top: 50%;
			left: 0;
			transform: translateY(-50%) translateX(${({ direction }) => (direction === 'left' ? '20px' : '6px')});
		}
	}
`;

export const CarouselBlock = styled(motion.div)`
	width: 100%;
	height: 100%;
	padding: 20px;
	display: grid;
	align-content: center;
	justify-content: flex-start;
	justify-items: start;
	position: absolute;
	grid-gap: 5px;
	text-align: left;
	grid-template-columns: minmax(100px, 55%);

	@media (min-width: ${BREAKPOINT_LG}) {
		grid-template-columns: minmax(500px, 55%);
		align-content: start;
		justify-content: flex-start;
		justify-items: start;
		padding: 40px 55px 20px;
		grid-gap: 15px;
	}
`;

export const CarouselImage = styled.img`
	width: 100%;
	height: 100%;
	position: absolute;
	pointer-events: none;
	object-fit: none;
	object-position: left;

	@media (min-width: ${BREAKPOINT_LG}) {
		object-fit: cover;
	}
`;

export const CarouselBackgroundImage = styled(CarouselImage)`
	object-fit: cover;
	object-position: center;
`;

export const CarouselBackgroundLazyImageContainer = styled.div`
	width: 100%;
	height: 100%;
	position: absolute;
	pointer-events: none;

	// control the children of LazyLoadImage component
	img {
		object-fit: cover;
		object-position: center;
	}
`;

export const CarouselIframe = styled.iframe`
	width: 100%;
	height: 100%;
	position: absolute;
`;

const foreGroundImageStyle = {
	width: 'auto',
	'max-height': '100%',
	'max-width': '100%'
};

export const CarouselForegroundImageContainer = styled.div`
	pointer-events: none;
	display: flex;
	align-items: center;
	justify-content: center;
	width: calc(40% - 10px);
	height: 100%;
	z-index: 1;

	//Test
	right: 20px;
	position: absolute;

	@media (min-width: ${BREAKPOINT_LG}) {
		position: absolute;
		width: 530px;
		max-width: calc(50% - 35px);
		right: 35px;
		height: 100%;
	}

	// control the children of LazyLoadImage component
	picture {
		display: flex;
		align-items: center;
		justify-content: center;
	}
	img {
		${foreGroundImageStyle};
		object-fit: contain;
	}
`;

export const CarouselForegroundImage = styled.img`
	${foreGroundImageStyle};
`;

export const CarouselOverlay = styled.div`
	position: absolute;
	width: 100%;
	height: 100%;
	background: linear-gradient(180deg, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0.75) 100%);
	opacity: 0.6;
	z-index: 1;
	@media (min-width: ${BREAKPOINT_LG}) {
		display: none;
	}
`;

export const CarouselTitle = styled.h1`
	font: var(--carouselTitleFont);
	color: rgba(var(--carouselTitleColor), 1);
	max-width: 100%;
	z-index: 1;
	text-align: left;

	@media (min-width: ${BREAKPOINT_LG}) {
		max-width: 700px;
		font: var(--carouselTitleFontLarge);
	}
`;

export const MultiLayerCarouselTitle = styled(CarouselTitle)`
	font: var(--multiLayerCarouselTitleFont);
	word-break: break-word;

	@media (min-width: ${BREAKPOINT_LG}) {
		font: var(--multiLayerCarouselTitleFontLarge);
	}
`;

export const CarouselDescription = styled.h2`
	font: var(--carouselDescFont);
	text-align: left;
	color: rgba(var(--carouselDescColor), 1);
	max-width: 100%;
	z-index: 1;

	@media (min-width: ${BREAKPOINT_LG}) {
		max-width: 700px;
		font: var(--carouselDescFontLarge);
	}
`;

export const MultiLayerCarouselDescription = styled(CarouselDescription)`
	font: var(--multiLayerCarouselDescFont);
	word-break: break-word;

	@media (min-width: ${BREAKPOINT_LG}) {
		font: var(--multiLayerCarouselDescFontLarge);
	}
`;

export const CarouselButton = styled(ButtonRoundedPrimary)`
	position: absolute;
	bottom: 30px;
	left: 55px;
	display: block;
	padding: 14px 45px;
	width: auto;
	z-index: 1;

	&:hover {
		background-color: var(--button-primary-bg-hover, rgba(var(--primary-light), 1));
	}
`;

export const MultiLayerCarouselButton = styled(CarouselButton)`
	position: unset;
	color: rgba(var(--multiLayerCarouselButtonColor), 1);
	font: var(--multiLayerCarouselButtonFont);
	text-transform: var(--brand-text-transform, none);
	padding: 4px 12px;
	margin-top: 5px;
	@media (min-width: ${BREAKPOINT_LG}) {
		bottom: 25px;
		font: var(--multiLayerCarouselButtonFontLarge);
		padding: 14px 20px;
		position: absolute;
	}
`;

export const AnimatedCarouselSwipeContainer = styled(motion.div)`
	position: absolute;
	height: 100%;
	width: 100%;
`;
